import React, { useState } from 'react';
import { useParams, Navigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

// Componentes
import Header from '../components/Header/Header';
import Footer from '../components/Footer/Footer';
import FloatingNavigation from '../components/TherapistProfile/FloatingNavigation';
import TherapistHero from '../components/TherapistProfile/TherapistHero';
import TherapistAbout from '../components/TherapistProfile/TherapistAbout';
import TherapistEducation from '../components/TherapistProfile/TherapistEducation';
import TherapistServices from '../components/TherapistProfile/TherapistServices';
import TherapistTestimonials from '../components/TherapistProfile/TherapistTestimonials';
import TherapistContact from '../components/TherapistProfile/TherapistContact';
import StructuredData from '../components/SEO/StructuredData';

// Datos y tipos
import { TherapistExtended } from '../types';
import { useTerapeutas } from '../hooks/useTerapeutas';
import { toAbsoluteUrl } from '../lib/utils/seoUrl';

// Schemas SEO
import { createTherapistPersonSchema, createBreadcrumbSchema } from '../data/schemas';

// Estilos
import '../components/TherapistProfile/TherapistPage.css';
import '../components/TherapistProfile/TherapistSections.css';
import '../components/TherapistProfile/FloatingNavigation.css';

// Tipos para las pestañas
type TabType = 'about' | 'education' | 'services' | 'testimonials' | 'contact';

/**
 * Página individual del terapista
 * Ruta: /terapista/:slug
 */
const TherapistPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const [activeTab, setActiveTab] = useState<TabType>('about');
  
  // Hook para obtener terapeutas de Supabase
  const { getTerapeutaBySlug, loading, error } = useTerapeutas();
  
  // Buscar terapista por slug desde Supabase
  const therapist: TherapistExtended | undefined = slug ? getTerapeutaBySlug(slug) : undefined;

  // Estados de carga
  if (loading) {
    return (
      <div className="page-loading-state">
        <i className="fas fa-spinner fa-spin"></i>
        <span>Cargando información del terapeuta...</span>
      </div>
    );
  }

  // Si hay error pero tenemos datos estáticos, mostrar warning
  if (error && !therapist) {
    console.warn('Error cargando desde Supabase, usando datos estáticos:', error);
  }

  // Si no se encuentra el terapista, redirigir a la página principal
  if (!therapist) {
    return <Navigate to="/" replace />;
  }

  // Función para cambiar de pestaña
  const handleTabChange = (tab: string) => {
    setActiveTab(tab as TabType);
  };

  // Configuración de pestañas
  const tabs = [
    { id: 'about' as TabType, label: `Sobre ${therapist.name.split(' ')[0]}`, icon: 'fas fa-user' },
    { id: 'education' as TabType, label: 'Formación', icon: 'fas fa-graduation-cap' },
    { id: 'services' as TabType, label: 'Servicios', icon: 'fas fa-stethoscope' },
    ...(therapist.pageConfig?.showTestimonials !== false ? [{ id: 'testimonials' as TabType, label: 'Testimonios', icon: 'fas fa-star' }] : []),
    { id: 'contact' as TabType, label: 'Contacto', icon: 'fas fa-envelope' }
  ];

  // Breadcrumb para SEO
  const breadcrumbItems = [
    { name: 'Inicio', url: '/' },
    { name: 'Profesionales', url: '/#professionals' },
    { name: therapist.name }
  ];

  return (
    <div className="therapist-page">
      {/* SEO específico del terapista */}
      <Helmet>
        <title>{therapist.seo.metaTitle}</title>
        <meta name="description" content={therapist.seo.metaDescription} />
        <meta name="keywords" content={therapist.seo.keywords.join(', ')} />
        <link rel="canonical" href={therapist.seo.canonicalUrl} />
        
        {/* Open Graph específico */}
        <meta property="og:title" content={therapist.seo.metaTitle} />
        <meta property="og:description" content={therapist.seo.metaDescription} />
        <meta property="og:image" content={toAbsoluteUrl(therapist.seo.ogImage || therapist.image)} />
        <meta property="og:url" content={therapist.seo.canonicalUrl} />
        <meta property="og:type" content="profile" />

        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={therapist.seo.metaTitle} />
        <meta name="twitter:description" content={therapist.seo.metaDescription} />
        <meta name="twitter:image" content={toAbsoluteUrl(therapist.seo.ogImage || therapist.image)} />
        
      </Helmet>

      {/* Datos estructurados mejorados - Sin impacto visual */}
      <StructuredData data={createTherapistPersonSchema(therapist)} />
      <StructuredData data={createBreadcrumbSchema(breadcrumbItems)} />

      {/* Header de navegación */}
      <Header />

      {/* Contenido principal */}
      <main className="therapist-main">
        {/* Hero Section del Terapista */}
        <TherapistHero therapist={therapist} />
        

        {/* Navegación por pestañas */}
        <nav className="therapist-tabs-nav" aria-label="Navegación de secciones">
          <div className="container">
            <ul className="tabs-list">
              {tabs.map((tab) => (
                <li key={tab.id} className="tab-item">
                  <button
                    className={`tab-link ${activeTab === tab.id ? 'active' : ''}`}
                    onClick={() => handleTabChange(tab.id)}
                    aria-current={activeTab === tab.id ? 'page' : undefined}
                  >
                    <i className={tab.icon}></i>
                    <span>{tab.label}</span>
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </nav>

        {/* Contenido dinámico basado en pestañas */}
        <div className="tab-content-wrapper">
          <div className={`tab-content ${activeTab === 'about' ? 'active' : ''}`}>
            {activeTab === 'about' && <TherapistAbout therapist={therapist} />}
          </div>

          <div className={`tab-content ${activeTab === 'education' ? 'active' : ''}`}>
            {activeTab === 'education' && <TherapistEducation therapist={therapist} />}
          </div>

          <div className={`tab-content ${activeTab === 'services' ? 'active' : ''}`}>
            {activeTab === 'services' && (
              <TherapistServices 
                services={therapist.services} 
                pricing={therapist.availability.prices}
                therapist={therapist}
              />
            )}
          </div>

          {therapist.pageConfig?.showTestimonials !== false && (
            <div className={`tab-content ${activeTab === 'testimonials' ? 'active' : ''}`}>
              {activeTab === 'testimonials' && <TherapistTestimonials therapist={therapist} />}
            </div>
          )}

          <div className={`tab-content ${activeTab === 'contact' ? 'active' : ''}`}>
            {activeTab === 'contact' && <TherapistContact therapist={therapist} />}
          </div>
        </div>

      </main>

      {/* Footer */}
      <Footer />

      {/* Navegación flotante móvil */}
      <FloatingNavigation 
        activeTab={activeTab}
        onTabChange={handleTabChange}
        tabs={tabs}
      />

    </div>
  );
};

export default TherapistPage;